import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";

const STATUS_COLOR = { active: "success", scheduled: "info", expired: "default", revoked: "default" };

const errorText = (e) =>
  typeof e?.response?.data === "string" ? e.response.data : "Something went wrong. Please try again.";

function ActingMayors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get("/api/acting-mayors")
      .then(({ data }) => setItems(data))
      .catch((e) => setError(errorText(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const revoke = (item) => {
    if (!window.confirm(`Revoke ${item.mayorNames} as ${item.positionName} of ${item.districtName}?`)) return;
    setError("");
    axios
      .post(`/api/acting-mayors/${item.id}/revoke`)
      .then(() => { setNotice("Acting mayor revoked."); load(); })
      .catch((e) => setError(errorText(e)));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Acting Mayors</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Show an acting mayor's name and position on transfer and permutation letters for a district,
        for a limited time. The substantive mayor record stays unchanged. The name appears on letters
        generated while the override is active, both as the signatory (district receiving the teacher)
        and in the copy list (district the teacher leaves). Letters already generated are not changed;
        generate them again to pick it up.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>{notice}</Alert>}

      <Button variant="contained" onClick={() => { setNotice(""); setOpen(true); }} sx={{ mb: 2 }}>
        Add acting mayor
      </Button>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>District</TableCell>
              <TableCell>Name on letters</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Set by</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">No acting mayors yet.</TableCell></TableRow>
            )}
            {items.map((i) => (
              <TableRow key={i.id}>
                <TableCell>{i.districtName}</TableCell>
                <TableCell>
                  {i.mayorNames}
                  <br />
                  <small>{i.positionName}</small>
                </TableCell>
                <TableCell>{moment(i.expiresAt).format("DD MMM YYYY")}</TableCell>
                <TableCell>{i.reason}</TableCell>
                <TableCell>
                  {i.createdByName}
                  <br />
                  <small>{moment(i.createdAt).format("DD MMM YYYY")}</small>
                  {i.revokedAt && (
                    <>
                      <br />
                      <small>Revoked by {i.revokedByName} on {moment(i.revokedAt).format("DD MMM YYYY")}</small>
                    </>
                  )}
                </TableCell>
                <TableCell><Chip size="small" label={i.status} color={STATUS_COLOR[i.status]} /></TableCell>
                <TableCell>
                  {(i.status === "active" || i.status === "scheduled") && (
                    <Button size="small" color="error" onClick={() => revoke(i)}>Revoke</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {open && (
        <AddDialog
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); setNotice("Acting mayor saved."); load(); }}
        />
      )}
    </Box>
  );
}

function AddDialog({ onClose, onSaved }) {
  const [districts, setDistricts] = useState([]);
  const [districtId, setDistrictId] = useState("");
  const [mayorNames, setMayorNames] = useState("");
  const [positionName, setPositionName] = useState("Acting Mayor");
  const [expiresAt, setExpiresAt] = useState(moment().add(30, "days").format("YYYY-MM-DD"));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get("/api/acting-mayors/districts").then(({ data }) => setDistricts(data)).catch(() => {});
  }, []);

  const save = () => {
    setError("");
    setSaving(true);
    axios
      .post("/api/acting-mayors", {
        districtId,
        mayorNames,
        positionName,
        // end of the chosen day, local time
        expiresAt: moment(expiresAt).endOf("day").toISOString(),
        reason,
      })
      .then(onSaved)
      .catch((e) => { setError(errorText(e)); setSaving(false); });
  };

  const valid =
    districtId && mayorNames.trim().length >= 3 && positionName.trim().length >= 3 &&
    expiresAt && reason.trim().length >= 5;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add acting mayor</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          select fullWidth size="small" label="District" margin="dense" value={districtId}
          onChange={(e) => setDistrictId(e.target.value)}
        >
          {districts.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
        <TextField
          fullWidth size="small" margin="dense" label="Name as it should appear on letters"
          value={mayorNames} onChange={(e) => setMayorNames(e.target.value)}
          inputProps={{ maxLength: 60 }}
        />
        <TextField
          fullWidth size="small" margin="dense" label="Position"
          value={positionName} onChange={(e) => setPositionName(e.target.value)}
          inputProps={{ maxLength: 60 }}
        />
        <TextField
          fullWidth size="small" margin="normal" type="date" label="Acting until"
          InputLabelProps={{ shrink: true }} value={expiresAt}
          inputProps={{ min: moment().add(1, "day").format("YYYY-MM-DD"), max: moment().add(180, "days").format("YYYY-MM-DD") }}
          onChange={(e) => setExpiresAt(e.target.value)}
          helperText="Maximum 180 days. You can revoke earlier."
        />
        <TextField
          fullWidth size="small" margin="dense" multiline minRows={2} label="Reason"
          value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 500 }}
          helperText="Do not include ID or phone numbers."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ActingMayors;
